import {
  BUSINESS,
  discountAmount,
  invoiceNetTotal,
  invoiceTotal,
  lineTotal,
} from '../../lib/invoices';
import { formatJMD, formatUSD, parseEntryDate } from '../../lib/finances';
import logo from '../../assets/logo.png';

// The invoice as the customer sees it — a direct rebuild of the paper
// template this section replaced, so a printed one is indistinguishable from
// the ones already sent out.
//
// Printing is the browser's own print-to-PDF, driven by an @media print block
// in Invoices.css, rather than a PDF library. It's one page of static layout;
// pulling in jsPDF to redraw a table the browser can already paginate would
// be a dependency and a second layout to keep in sync with this one.
//
// MIN_ROWS keeps the empty ruled rows from the paper form. They aren't
// decoration: the printed grid is a fixed block on the page, and an invoice
// with three lines should occupy the same space as one with eight.
const MIN_ROWS = 8;

// dd/mm/yyyy, as on the template — and via parseEntryDate, because a bare
// "YYYY-MM-DD" through new Date() is UTC midnight, which in Jamaica renders
// as the previous day.
function shortDate(value) {
  if (!value) return '';
  const d = parseEntryDate(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function longDate(value) {
  if (!value) return '';
  return parseEntryDate(value).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function InvoicePrint({ invoice }) {
  const items = invoice.invoice_items || [];
  const money = invoice.currency === 'USD' ? formatUSD : formatJMD;
  const blanks = Math.max(0, MIN_ROWS - items.length);
  const discount = discountAmount(invoice, items);

  return (
    <article className="invoice-sheet">
      <header className="invoice-sheet-head">
        <div>
          <h2 className="invoice-sheet-title">INVOICE</h2>

          <div className="invoice-sheet-field">
            <span>Date:</span>
            <strong>{longDate(invoice.issue_date)}</strong>
          </div>
          <div className="invoice-sheet-field">
            <span>No. Invoice :</span>
            <strong>{invoice.invoice_number}</strong>
          </div>
          <div className="invoice-sheet-field">
            <span>Bill to:</span>
            <strong>{invoice.bill_to_name}</strong>
          </div>
          {invoice.bill_to_address && (
            <p className="invoice-sheet-address">{invoice.bill_to_address}</p>
          )}
          {invoice.bill_to_email && <p className="invoice-sheet-address">{invoice.bill_to_email}</p>}
        </div>

        <div className="invoice-sheet-brand">
          <img src={logo} alt={BUSINESS.name} />
          <dl className="invoice-sheet-bank">
            <div>
              <dt>Payment Method:</dt>
              <dd>{invoice.payment_method || BUSINESS.bank.method}</dd>
            </div>
            <div>
              <dt>Bank Name:</dt>
              <dd>{BUSINESS.bank.bankName}</dd>
            </div>
            <div>
              <dt>Name:</dt>
              <dd>{BUSINESS.bank.accountName}</dd>
            </div>
            <div>
              <dt>Account Number:</dt>
              <dd>{BUSINESS.bank.accountNumber}</dd>
            </div>
            <div>
              <dt>Type:</dt>
              <dd>{BUSINESS.bank.accountType}</dd>
            </div>
          </dl>
        </div>
      </header>

      <table className="invoice-sheet-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Item Description</th>
            <th>Price</th>
            <th>Qty</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id || item.description}>
              <td>{shortDate(item.item_date || invoice.issue_date)}</td>
              <td>{item.description}</td>
              <td>{Number(item.unit_price)}</td>
              <td>{Number(item.quantity)}</td>
              <td>{lineTotal(item)}</td>
            </tr>
          ))}
          {Array.from({ length: blanks }, (_, i) => (
            <tr key={`blank-${i}`} className="invoice-sheet-blank">
              <td colSpan={5}>&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>

      {invoice.notes && <p className="invoice-sheet-notes">{invoice.notes}</p>}

      <footer className="invoice-sheet-foot">
        <div>
          <p className="invoice-sheet-thanks">THANK YOU!</p>
          <ul className="invoice-sheet-contact">
            {BUSINESS.phones.map((phone) => (
              <li key={phone}>{phone}</li>
            ))}
            <li>{BUSINESS.emails.join(' | ')}</li>
            <li>{BUSINESS.website}</li>
          </ul>
        </div>

        <div className="invoice-sheet-total">
          {/* Subtotal and discount are printed only when there is a discount,
              so an ordinary invoice looks exactly like the paper one it
              replaced. When there is one, the customer must be able to see
              what came off — a total that is simply lower than the lines add
              up to is the thing that gets queried. */}
          {discount > 0 && (
            <>
              <span className="invoice-sheet-subtotal">Subtotal:</span>
              <em>{money(invoiceTotal(items))}</em>
              <span className="invoice-sheet-subtotal">
                Discount
                {invoice.discount_type === 'Percent' && ` (${Number(invoice.discount_value)}%)`}:
              </span>
              <em>− {money(discount)}</em>
            </>
          )}
          <span>Total:</span>
          <strong>{money(invoiceNetTotal(invoice, items))}</strong>
        </div>
      </footer>
    </article>
  );
}
